import { ApiClient, ApiClientConfig } from "../api-client";
import { ApiClientError } from "../errors";
import { ApiClientProctoringOptionV2 } from "../types";
import schemaValidators from "./schema-validators";

export default class ProctoringProfileClient extends ApiClient<typeof schemaValidators> {
	constructor(config: ApiClientConfig) {
		super(config, schemaValidators);
	}

	getAll = async ({ lmsProvidedCourseId }: { lmsProvidedCourseId?: string }) => {
		try {
			const url = new URL(`/api/verity/v2/profile`, this.config.verityOrigin);
			if (lmsProvidedCourseId) {
				url.searchParams.set("lmsProvidedCourseId", lmsProvidedCourseId);
			}
			const response = await super.fetch(url, {
				credentials: "include",
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.profiles.validate(obj)) {
					return obj;
				}
			}
		} 
		catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	create = async ({
		proctoringOption,
		isInstituteProfile,
		lmsProvidedCourseId,
		name,
	}: {
		proctoringOption: Partial<ApiClientProctoringOptionV2>;
		isInstituteProfile?: boolean;
		lmsProvidedCourseId?: string;
		name?: string;
	}) => {
		try {
			const response = await super.fetch(new URL(`/api/verity/v2/profile`, this.config.verityOrigin), {
				headers: {
					"Content-Type": "application/json",
				},
				method: "POST",
				credentials: "include",
				body: JSON.stringify({
					proctoringOption: proctoringOption,
					isInstituteProfile: isInstituteProfile,
					lmsProvidedCourseId: lmsProvidedCourseId,
					name: name,
				}),
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.profile.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	update = async ({
		id,
		proctoringOption,
		name,
	}: {
		id: number;
		proctoringOption: Partial<ApiClientProctoringOptionV2>;
		name?: string;
	}) => {
		try {
			const response = await super.fetch(new URL(`/api/verity/v2/profile/${id}`, this.config.verityOrigin), {
				headers: {
					"Content-Type": "application/json",
				},
				credentials: "include",
				method: "PATCH",
				body: JSON.stringify({
					proctoringOption: proctoringOption,
					name: name,
				}),
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.profile.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	delete = async (id: number) => {
		try {
			const response = await super.fetch(new URL(`/api/verity/v2/profile/${id}`, this.config.verityOrigin), {
				headers: {
					"Content-Type": "application/json",
				},
				credentials: "include",
				method: "DELETE",
			});
			if (response.ok) {
				return;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	enforce = async ({
		enforceProfileIds,
		unenforceProfileIds,
	}: {
		enforceProfileIds?: Array<number>;
		unenforceProfileIds?: Array<number>;
	}) => {
		try {
			const response = await super.fetch(new URL(`/api/verity/v2/profiles/enforce`, this.config.verityOrigin), {
				headers: {
					"Content-Type": "application/json",
				},
				credentials: "include",
				method: "PATCH",
				body: JSON.stringify({
					enforceProfileIds: enforceProfileIds,
					unenforceProfileIds: unenforceProfileIds,
				}),
			});
			if (response.ok) {
				return;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	profilesEnforced = async () => {
		try {
			const response = await super.fetch(new URL(`/api/verity/v2/profiles/enforce`, this.config.verityOrigin), {
				credentials: "include",
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.enforced.validate(obj)) {
					return obj.enforced;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getDefault = async () => {
		try {
			const response = await super.fetch(new URL(`/api/verity/v2/profile/default`, this.config.verityOrigin), {
				credentials: "include",
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.profileOrNull.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	setDefault = async (proctoringOptionProfileId: number | null) => {
		try {
			const response = await super.fetch(new URL(`/api/verity/v2/profile/default`, this.config.verityOrigin), {
				headers: {
					"Content-Type": "application/json",
				},
				credentials: "include",
				method: "PATCH",
				body: JSON.stringify({
					proctoringOptionProfileId,
				}),
			});
			if (response.ok) {
				return;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};
}
